import { Checkbox, Col, Form, Row, Switch } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import dayjs from "dayjs";
import { RangeValue } from "rc-picker/lib/interface";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RangePicker } from "../../../../components/DatePicker/DatePicker";
import { Input } from "../../../../components/Input/Input";
import { Modal } from "../../../../components/Modal/Modal";
import { ModalsContext } from "../../../../context/modals.context";
import { DATE_FORMAT } from "../../../../core/constants";
import {
  IObveznikGrupe,
  IUserDetails,
} from "../../../../modules/settings/settings.types";

import { MandatoryField } from "../../../../components/MandatoryField/MandatoryField";
import { Select } from "../../../../components/Select/Select";
import style from "./CompanyUserModal.module.scss";

export interface ICompanUserModalProps {
  selectedUser: IUserDetails | null;
  clearUser: () => void;
  editCompanyUser: (user: IUserDetails) => Promise<void>;
  createCompanyUser: (user: IUserDetails) => Promise<void>;
  roleGroups: IObveznikGrupe[];
}

export const CompanyUserModal: FC<ICompanUserModalProps> = ({
  selectedUser,
  clearUser,
  editCompanyUser,
  createCompanyUser,
  roleGroups,
}) => {
  const { isModalVisible, clearModals } = useContext(ModalsContext);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const [form] = useForm();
  const userGroup = useWatch("uloga", form);
  const userRoles = useWatch("roles", form);
  const userName = useWatch("ime", form);
  const userSurname = useWatch("prezime", form);
  const userEmail = useWatch("emailAdress", form);
  const canSave =
    userEmail &&
    userName &&
    userSurname &&
    userRoles?.some((role: { id: number | null }) => !!role?.id);

  useEffect(() => {
    if (selectedUser) {
      const userRoles =
        roleGroups.find((rGroup) => rGroup.ulogaId === selectedUser.ulogaId)
          ?.permise || [];

      form.setFieldsValue({
        ime: selectedUser.ime,
        prezime: selectedUser.prezime,
        uloga: selectedUser.ulogaId,
        emailAdress: selectedUser.emailAdress,
        aktivno: selectedUser.aktivno,
        roles: userRoles.map((r) => {
          const hasRole = selectedUser.roles.find((role) => r.id === role.id);
          return {
            datumVazenjaOd: hasRole ? hasRole.datumVazenjeOd : null,
            datumVazenjaDo: hasRole ? hasRole.datumVazenjeDo : null,
            id: hasRole ? r.id : null,
          };
        }),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        aktivno: true,
      });
    }
  }, [selectedUser]);

  const handleOk = () => {
    form.submit();
  };

  const handleSubmit = async (values: IUserDetails) => {
    setIsLoading(true);
    try {
      if (selectedUser) {
        await editCompanyUser({ ...values, id: selectedUser.id });
      } else {
        await createCompanyUser(values);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }

    setTimeout(() => {
      form.resetFields();
      clearUser();
    }, 100);
  };

  const handleCancel = () => {
    clearModals();

    // wait for modal close animation
    setTimeout(() => {
      if (selectedUser) {
        clearUser();
      }
      form.resetFields();
    }, 100);
  };

  const title = selectedUser
    ? `${selectedUser.ime} ${selectedUser.prezime}`
    : t("SETTINGS_USERS.NOVI_KORISNIK");

  const getDefaultDateValues = (index: number): RangeValue<dayjs.Dayjs> => {
    const datumOd = form.getFieldValue(["roles", index, "datumVazenjaOd"]);
    const datumDo = form.getFieldValue(["roles", index, "datumVazenjaDo"]);

    return [
      datumOd ? dayjs(datumOd, DATE_FORMAT) : null,
      datumDo ? dayjs(datumDo, DATE_FORMAT) : null,
    ];
  };

  // INFO: Groups are ommited for now
  //       BE will now always return only one group
  //       and then we render its permissions
  // const groupOptions = roleGroups.map((group) => ({
  //   label: group.uloga,
  //   value: group.ulogaId,
  // }));

  const selectedGroupRoles = roleGroups[0]?.permise || [];

  const disableEverythingButEmail = !!(
    selectedUser && selectedUser.onlyEmailEdit
  );

  const parseRolesOnChangeGrupa = () => {
    if (selectedUser) {
      const groupRoles =
        roleGroups.find((rGroup) => rGroup.ulogaId === userGroup)?.permise ||
        [];
      form.setFieldsValue({
        roles: groupRoles.map((r) => {
          const hasRole = selectedUser.roles.find((role) => r.id === role.id);
          return {
            datumVazenjaOd: hasRole ? hasRole.datumVazenjeOd : null,
            datumVazenjaDo: hasRole ? hasRole.datumVazenjeDo : null,
            id: hasRole ? r.id : null,
          };
        }),
      });
    }
  };

  useEffect(() => {
    parseRolesOnChangeGrupa();
  }, [userGroup]);

  return (
    <Modal
      name="company-user-modal"
      title={title}
      onOk={handleOk}
      cancelText={t("SETTINGS_COMPANY.NAZAD")}
      width={1200}
      cancelButtonProps={{ type: "default" }}
      okText={t("SETTINGS_COMPANY.SACUVAJ")}
      confirmLoading={isLoading}
      onCancel={handleCancel}
      okButtonProps={{ disabled: !canSave }}
      open={isModalVisible("company_user")}
      destroyOnClose={true}
      forceRender
    >
      <Form
        form={form}
        labelCol={{ span: 8 }}
        labelAlign="right"
        onFinish={handleSubmit}
        preserve={false}
        initialValues={
          selectedUser
            ? selectedUser
            : {
                aktivno: true,
              }
        }
      >
        <div className={style.form}>
          <div className="w-25">
            <Form.Item
              name="ime"
              colon={false}
              label={MandatoryField("COMMON.IME")}
            >
              <Input name="user-name" disabled={disableEverythingButEmail} />
            </Form.Item>
            <Form.Item
              name="prezime"
              colon={false}
              label={MandatoryField("COMMON.PREZIME")}
            >
              <Input name="user-surname" disabled={disableEverythingButEmail} />
            </Form.Item>
            <Form.Item
              name="emailAdress"
              colon={false}
              label={MandatoryField("COMMON.EMAIL")}
            >
              <Input name="user-email" />
            </Form.Item>
            <Form.Item
              name="aktivno"
              colon={false}
              labelCol={{ span: 8 }}
              label={t("COMMON.AKTIVNO")}
              valuePropName="checked"
            >
              <Switch disabled={disableEverythingButEmail} />
            </Form.Item>
          </div>
          <div className="w-70">
            <Row gutter={12} style={{ display: "none" }}>
              <Col span={10}>
                <Form.Item
                  name="uloga"
                  colon={false}
                  labelCol={{ span: 8 }}
                  label={t("COMMON.GRUPA")}
                >
                  <Select
                    name="user-froup"
                    disabled={disableEverythingButEmail}
                  />
                </Form.Item>
              </Col>
            </Row>
            {selectedGroupRoles.map((role, i) => (
              <Row gutter={12} key={role.name}>
                <Col
                  span={14}
                  style={{ alignSelf: "flex-end" }}
                  className={style.roleColumn}
                >
                  <Form.Item
                    label={role.name}
                    colon={false}
                    labelCol={{ span: 23 }}
                    valuePropName="checked"
                    name={["roles", i, "id"]}
                  >
                    <Checkbox
                      disabled={disableEverythingButEmail}
                      onChange={(e) => {
                        form.setFieldValue(
                          ["roles", i, "id"],
                          e.target.checked ? role.id : null
                        );
                      }}
                    ></Checkbox>
                  </Form.Item>
                </Col>
                <Form.Item
                  name={["roles", i, "datumVazenjaOd"]}
                  style={{ display: "none" }}
                ></Form.Item>
                <Form.Item
                  name={["roles", i, "datumVazenjaDo"]}
                  style={{ display: "none" }}
                ></Form.Item>
                <Col span={10} className={style.roleColumn}>
                  <Form.Item shouldUpdate labelCol={{ span: 10 }}>
                    {() => (
                      <RangePicker
                        format={DATE_FORMAT}
                        value={getDefaultDateValues(i)}
                        name="datum-vazenja-do"
                        allowEmpty={[true, true]}
                        disabled={
                          !form.getFieldValue(["roles", i, "id"]) ||
                          disableEverythingButEmail
                        }
                        placeholder={[
                          t("PLACEHOLDERS.OD"),
                          t("PLACEHOLDERS.DO"),
                        ]}
                        onChange={(e) => {
                          form.setFieldValue(
                            ["roles", i, "datumVazenjaOd"],
                            e?.[0]
                          );
                          form.setFieldValue(
                            ["roles", i, "datumVazenjaDo"],
                            e?.[1]
                          );
                        }}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            ))}
          </div>
        </div>
      </Form>
    </Modal>
  );
};
